import { useEffect, useRef } from 'react';

// Sections whose defaults come from the selected parent project.
const PARENT_DERIVED_FIELDS = [
  'alerts',
  'alertConfirmations',
  'products',
  'packages',
  'customFields',
  'modules'
];

const pickParentDerived = (values = {}) =>
  PARENT_DERIVED_FIELDS.reduce((acc, field) => {
    acc[field] = values[field] ?? [];
    return acc;
  }, {});

/**
 * Keeps the form in sync with initialValues while they are still being fetched.
 * In edit mode the whole form is re-seeded as long as the user has not started
 * editing. In add mode only the parent-derived sections are re-seeded, and only
 * when one of them actually changed, so values typed in the meantime survive.
 */
export const useInitialValuesSync = (methods, initialValues, isEditMode) => {
  const { reset, setValue, formState: { isDirty } } = methods;
  const serializedRef = useRef(null);

  useEffect(() => {
    if (!initialValues) return;

    const parentDerived = pickParentDerived(initialValues);
    const serialized = JSON.stringify(isEditMode ? initialValues : parentDerived);
    if (serializedRef.current === serialized) return;
    serializedRef.current = serialized;

    if (isEditMode) {
      if (!isDirty) reset(initialValues);
      return;
    }

    Object.entries(parentDerived).forEach(([field, value]) => {
      setValue(field, value, { shouldDirty: false });
    });
  }, [initialValues, isEditMode, isDirty, reset, setValue]);
};

/**
 * Reports the selected parent project upwards, so the add container can fetch
 * the alerts, products, packages and custom fields that belong to it.
 */
export const useParentProjectWatch = (parentId, onParentProjectChange) => {
  useEffect(() => {
    onParentProjectChange?.(parentId);
  }, [parentId, onParentProjectChange]);
};
